import { useLocation, Link } from 'react-router-dom';
import { highlight, formatDate } from '../lib/utils';
import type { Rental } from '../lib/rental';
import RentalMediaCarousel from './RentalMediaCarousel';
import { HeartIcon as Heart } from '@heroicons/react/24/outline';
import { useRentalContext } from '../contexts/RentalContext';
import { formatPrice, getDisplayPrice } from '../lib/price';
import useStore from '../lib/store';
import useCurrencies from '../hooks/useCurrencies';

const RentalCard = ({ rental }: { rental: Rental }) => {
	const exchangeRates = useCurrencies()
	// TODO: do I want to use the Rental Context? Or store?
	const { displayCurrency, keywordTerms } = useRentalContext();
	const likedRentalIds = useStore((state => state.likedRentalIds));

	const location = useLocation();
	const toggleLike = useStore((state) => state.toggleLike);
	const setMap = useStore((state) => state.setMap);

	return (
		<div 
			data-testid="rental-card" 
			className="relative bg-white flex flex-col w-full"
			onMouseEnter={() => setMap({ hoveredLocation: rental.location })}
			onMouseLeave={() => setMap({ hoveredLocation: null })}
		>
			<div className="relative">
				<button
					data-umami-event="Like button clicked"
					onClick={() => toggleLike(rental)}
					className="absolute top-2 right-2 z-10"
					aria-label="Toggle favorite"
				>
					{likedRentalIds.some((id) => id === rental.id) ? <Heart className="size-7 text-white fill-red-500" /> : <Heart className="size-7 text-white fill-black/50" />}
				</button>
				<div data-umami-event="Details opened">
					<Link
						to={`/rental/${rental.slug || rental.id}`}
						state={{ displayCurrency, keywordTerms, backgroundLocation: location }}
						className="relative block group"
					>
						<RentalMediaCarousel mediaUrls={rental.media_urls && rental.media_urls.length > 0 ?
							rental.media_urls : ["placeholder.png"]} useThumbnail={true} />

						<h3 className="text-lg text-gray-800 mt-3">{highlight(rental.title ?? 'Rental property', keywordTerms ?? [])}</h3>
						<p className="text-md text-gray-400">{rental.location}</p>
						<p className="text-md text-gray-400">
							{[prettyText(rental.bedrooms, 'bedroom'), prettyText(rental.bathrooms, 'bathroom')].filter(Boolean).join(' · ')}
						</p>
						<p className="flex">
							<span className="flex-1 text-sm text-black underline">
								{formatPrice(getDisplayPrice(rental, exchangeRates, displayCurrency), displayCurrency)} / month
							</span>
							<span className="text-xs text-gray-400">{formatDate(rental.posted_at_source)}</span>
						</p>
					</Link>
				</div>
			</div>
		</div>
	);
};

const prettyText = (number: number | undefined, name: string) => {
	if (!number) return '';
	return `${number} ${name}${number > 1 ? 's' : ''}`;
}

export default RentalCard;
